import React, { useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  IconButton,
  Button,
  Tooltip,
  useTheme,
} from "@mui/material";
import {
  Edit,
  Delete,
  WhatsApp,
  OpenInNew,
  AddLink,
  Link as LinkIcon,
} from "@mui/icons-material";
import { EyeOff, ShieldCheck, ShieldAlert } from "lucide-react";

// Swiper imports
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { Product } from "./manageProducts";
import { getWhatsAppUrl } from "../../utils/whatsappHelper";
import { AmazonIcon } from "../../utils/amazonHelper";
import AmazonLinkModal from "../../Components/Common/AmazonLinkModal";

interface ProductCardProps {
  product: Product;
  phoneNumber?: string;
  onEdit: (product: Product) => void;
  onDelete: (productId: string) => void;
  onUpdateAmazonLink?: (productId: string, amazonLink: string) => Promise<void> | void;
  onToggleStock?: (productId: string, inStock: boolean) => Promise<void> | void;
  onToggleWarranty?: (productId: string, hasWarranty: boolean) => Promise<void> | void;
}

const ProductCard: React.FC<ProductCardProps> = ({
  product,
  phoneNumber,
  onEdit,
  onDelete,
  onUpdateAmazonLink,
  onToggleStock,
  onToggleWarranty,
}) => {
  const theme = useTheme();
  const [openAmazonModal, setOpenAmazonModal] = useState(false);
  const isInStock = product.inStock !== false && product.in_stock !== false;
  const hasWarranty = Boolean(product.hasWarranty || product.has_warranty);
  const warrantyText =
    product.warrantyPeriod || product.warranty_period || "1 Year Warranty";

  const handleWhatsAppClick = () => {
    if (!phoneNumber) return;

    const message = `Hi! I'm interested in ${product.name} (₹${product.price}) from your store.`;
    const whatsappUrl = getWhatsAppUrl(phoneNumber, message);
    if (whatsappUrl) {
      window.open(whatsappUrl, "_blank");
    }
  };

  const amazonUrl = (product.amazonLink || product.amazon_link || "").trim();

  const handleOrderNowClick = () => {
    if (amazonUrl) {
      window.open(amazonUrl, "_blank", "noopener,noreferrer");
    } else {
      setOpenAmazonModal(true);
    }
  };

  const images =
    product.images?.length > 0
      ? product.images
      : ["https://via.placeholder.com/300x180?text=No+Image"];

  const hasAmazonLink = Boolean(amazonUrl);

  return (
    <>
      <Card
        sx={{
          borderRadius: 3,
          border: "1px solid #e0e0e0",
          width: "100%",
          maxWidth: 300,
          boxShadow: "0px 3px 6px rgba(0,0,0,0.1)",
          overflow: "visible",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          transition: "transform 0.2s ease, box-shadow 0.2s ease",
          "&:hover": {
            boxShadow: "0px 6px 16px rgba(0,0,0,0.12)",
          },
        }}
      >
        {/* Product Image Carousel */}
        <Box
          sx={{
            height: 180,
            position: "relative",
            "& .swiper": {
              borderRadius: "12px 12px 0 0",
              overflow: "hidden",
            },
            "& .swiper-button-next, .swiper-button-prev": {
              color: "#fff",
              background: "rgba(0,0,0,0.5)",
              width: 30,
              height: 30,
              borderRadius: "50%",
              "&:after": { fontSize: "16px" },
            },
            "& .swiper-pagination-bullet": {
              background: "#fff",
              opacity: 0.6,
            },
            "& .swiper-pagination-bullet-active": {
              background: "#2196f3",
              opacity: 1,
            },
          }}
        >
          <Swiper
            pagination={{ clickable: true }}
            navigation={images.length > 1}
            modules={[Pagination, Navigation, Autoplay]}
            loop={images.length > 1}
            autoplay={{ delay: 3000, disableOnInteraction: false }}
            style={{ height: "100%", width: "100%" }}
          >
            {images.map((image, index) => (
              <SwiperSlide key={index}>
                <img
                  src={image}
                  alt={`${product.name} - slide ${index + 1}`}
                  crossOrigin="anonymous"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://via.placeholder.com/300x180?text=No+Image";
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>

          {!isInStock && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                zIndex: 10,
                bgcolor: "rgba(220, 38, 38, 0.92)",
                color: "#ffffff",
                px: 1.25,
                py: 0.35,
                borderRadius: 1.5,
                fontSize: "0.7rem",
                fontWeight: 800,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
              }}
            >
              Out of Stock
            </Box>
          )}

          {hasWarranty && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                zIndex: 10,
                bgcolor: "rgba(30, 64, 175, 0.92)",
                color: "#ffffff",
                px: 1,
                py: 0.35,
                borderRadius: 1.5,
                fontSize: "0.68rem",
                fontWeight: 800,
                letterSpacing: "0.03em",
                boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.4,
              }}
            >
              <ShieldCheck size={12} />
              Warranty
            </Box>
          )}
        </Box>

        <CardContent sx={{ flex: 1, display: "flex", flexDirection: "column", p: 2 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: "bold",
              mb: 0.5,
              fontSize: "1rem",
              lineHeight: 1.3,
            }}
          >
            {product.name}
          </Typography>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1.5,
              fontSize: "0.85rem",
              minHeight: 38,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {product.description}
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
            }}
          >
            <Typography
              variant="h6"
              sx={{ color: "#1E88E5", fontWeight: "bold", fontSize: "1.1rem" }}
            >
              ₹{product.price.toLocaleString()}
            </Typography>

            <Tooltip
              title={
                onToggleStock
                  ? isInStock
                    ? "In Stock — Click to mark Out of Stock"
                    : "Out of Stock — Click to mark In Stock"
                  : isInStock
                  ? "In Stock"
                  : "Out of Stock"
              }
            >
              <Box
                onClick={
                  onToggleStock
                    ? () => onToggleStock(product._id, !isInStock)
                    : undefined
                }
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.6,
                  px: 1,
                  py: 0.35,
                  borderRadius: 999,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  cursor: onToggleStock ? "pointer" : "default",
                  bgcolor: isInStock ? "#ecfdf5" : "#fef2f2",
                  color: isInStock ? "#059669" : "#dc2626",
                  border: `1px solid ${isInStock ? "#a7f3d0" : "#fecaca"}`,
                  userSelect: "none",
                  transition: "all 0.15s ease",
                  "&:hover": onToggleStock
                    ? {
                        bgcolor: isInStock ? "#d1fae5" : "#fee2e2",
                        transform: "scale(1.03)",
                      }
                    : {},
                }}
              >
                <Box
                  sx={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    bgcolor: isInStock ? "#10b981" : "#ef4444",
                  }}
                />
                {isInStock ? "In Stock" : "Out of Stock"}
              </Box>
            </Tooltip>
          </Box>

          {/* Warranty Status & Quick Toggle */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
              px: 1,
              py: 0.6,
              borderRadius: 2,
              bgcolor: hasWarranty ? "#eff6ff" : "#f8fafc",
              border: `1px solid ${hasWarranty ? "#bfdbfe" : "#e2e8f0"}`,
              transition: "all 0.15s ease",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
              {hasWarranty ? (
                <ShieldCheck size={15} color="#2563eb" />
              ) : (
                <ShieldAlert size={15} color="#94a3b8" />
              )}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.74rem",
                  fontWeight: 700,
                  color: hasWarranty ? "#1e40af" : "#64748b",
                }}
              >
                {hasWarranty ? warrantyText : "No Warranty"}
              </Typography>
            </Box>

            {onToggleWarranty && (
              <Tooltip
                title={
                  hasWarranty
                    ? "Click to disable warranty for this product"
                    : "Click to enable warranty for this product"
                }
              >
                <Button
                  size="small"
                  variant={hasWarranty ? "text" : "outlined"}
                  onClick={() => onToggleWarranty(product._id, !hasWarranty)}
                  sx={{
                    minWidth: "auto",
                    py: 0.2,
                    px: 0.8,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    textTransform: "none",
                    borderRadius: 1.25,
                    color: hasWarranty ? "#dc2626" : "#2563eb",
                    borderColor: hasWarranty ? "transparent" : "#93c5fd",
                    bgcolor: hasWarranty ? "rgba(239, 68, 68, 0.08)" : "#ffffff",
                    "&:hover": {
                      bgcolor: hasWarranty
                        ? "rgba(239, 68, 68, 0.16)"
                        : "#eff6ff",
                    },
                  }}
                >
                  {hasWarranty ? "Disable" : "Enable"}
                </Button>
              </Tooltip>
            )}
          </Box>

          {/* Order Now button section */}
          <Box sx={{ mb: 1.5 }}>
            {hasAmazonLink ? (
              <Box>
                <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="small"
                    onClick={handleOrderNowClick}
                    startIcon={<AmazonIcon size={16} color="#111827" />}
                    endIcon={<OpenInNew sx={{ fontSize: "14px !important" }} />}
                    sx={{
                      bgcolor: "#FF9900",
                      color: "#111827",
                      fontWeight: 700,
                      textTransform: "none",
                      fontSize: "0.82rem",
                      borderRadius: 2,
                      py: 0.6,
                      boxShadow: "0 2px 6px rgba(255, 153, 0, 0.25)",
                      "&:hover": {
                        bgcolor: "#e68a00",
                        boxShadow: "0 4px 10px rgba(255, 153, 0, 0.35)",
                      },
                    }}
                  >
                    Order Now
                  </Button>

                  <Tooltip title="Edit Amazon Link">
                    <IconButton
                      size="small"
                      onClick={() => setOpenAmazonModal(true)}
                      sx={{
                        border: "1px solid #fed7aa",
                        bgcolor: "#fff7ed",
                        borderRadius: 2,
                        p: 0.65,
                        color: "#ea580c",
                        "&:hover": { bgcolor: "#ffedd5" },
                      }}
                    >
                      <LinkIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 0.75,
                    px: 0.25,
                  }}
                >
                  <Typography
                    variant="caption"
                    onClick={() => setOpenAmazonModal(true)}
                    sx={{
                      color: "#16a34a",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.3,
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    ✓ Amazon Link Active
                  </Typography>

                  <Typography
                    variant="caption"
                    onClick={() => setOpenAmazonModal(true)}
                    sx={{
                      color: "#ea580c",
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      cursor: "pointer",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    Change Link
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Button
                variant="outlined"
                fullWidth
                size="small"
                onClick={() => setOpenAmazonModal(true)}
                startIcon={<AmazonIcon size={16} color="#d97706" />}
                endIcon={<AddLink sx={{ fontSize: "16px !important" }} />}
                sx={{
                  borderStyle: "dashed",
                  borderWidth: 1.5,
                  borderColor: "#f59e0b",
                  color: "#b45309",
                  bgcolor: "#fffbeb",
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: "0.8rem",
                  borderRadius: 2,
                  py: 0.6,
                  "&:hover": {
                    bgcolor: "#fef3c7",
                    borderColor: "#d97706",
                    borderWidth: 1.5,
                  },
                }}
              >
                Order Now · Add Amazon Link
              </Button>
            )}
          </Box>

          {/* Action buttons (Hidden badge, Edit, WhatsApp, Delete) */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: "auto",
              pt: 1,
              borderTop: "1px solid #f1f5f9",
            }}
          >
            {product.ishide ? (
              <Box
                display="flex"
                alignItems="center"
                gap={0.5}
                sx={{
                  fontWeight: "bold",
                  color: "#64748b",
                  fontSize: "0.75rem",
                }}
              >
                <EyeOff size={15} />
                Hidden
              </Box>
            ) : (
              <Box sx={{ height: 24 }} />
            )}

            <Box sx={{ display: "flex", gap: 0.25 }}>
              <Tooltip title="Edit Product">
                <IconButton
                  color="primary"
                  onClick={() => onEdit(product)}
                  size="small"
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>

              <Tooltip title="WhatsApp Inquiry">
                <IconButton
                  color="success"
                  size="small"
                  onClick={handleWhatsAppClick}
                >
                  <WhatsApp
                    sx={{ color: theme.palette.success.main, fontSize: "20px" }}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete Product">
                <IconButton
                  color="error"
                  onClick={() => onDelete(product._id)}
                  size="small"
                >
                  <Delete
                    sx={{ color: theme.palette.error.main, fontSize: "20px" }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Amazon Link Modal Dialog */}
      <AmazonLinkModal
        open={openAmazonModal}
        onClose={() => setOpenAmazonModal(false)}
        product={product}
        onSave={async (id, link) => {
          if (onUpdateAmazonLink) {
            await onUpdateAmazonLink(id, link);
          }
        }}
      />
    </>
  );
};

export default ProductCard;
